// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type CSSObject, styled } from '@mui/material/styles';
import { getPaymentStatus, utils } from '@seedplanner/shared-utils';
import * as Sentry from '@sentry/react';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useLocation, useNavigate } from 'react-router';
import PageError from '../../components/common/error/PageError';
import { AccountOrLoginButton } from '../../components/login';
import { AppFooter } from '../../components/main/appFooter';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar as MuiAppBar,
  Drawer as MuiDrawer,
  Toolbar,
  Typography,
} from '../../lib/base';
import {
  AgricultureIcon,
  ArticleIcon,
  AssessmentIcon,
  AutoAwesomeIcon,
  AutorenewRoundedIcon,
  CalculateIcon,
  CalendarMonthIcon,
  CompostIcon,
  DashboardIcon,
  GrassIcon,
  MarginIcon,
  MenuIcon,
  MenuOpenIcon,
  MoreHorizIcon,
  PriceChangeIcon,
  ScheduleIcon,
  ShelvesIcon,
  ShoppingCartIcon,
  TaskIcon,
  WarehouseIcon,
} from '../../lib/icons';
import * as routes from '../../routes';
import type { Theme } from '../../theme/Theme';
import { Chevron } from '../animated';

const DRAWER_WIDTH = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: 'none',
  borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
  // This should always be the same as the theme-color meta tag below
  backgroundColor: '#001F3F',
}));

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open ? openedMixin(theme) : closedMixin(theme)),
  '& .MuiDrawer-paper': {
    background: 'linear-gradient(180deg, #002747, #001F3F)',
    color: 'rgba(255, 255, 255, 0.9)',
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },
}));

interface NavigationItem {
  readonly label: string;
  readonly icon: React.ReactElement;
  readonly isSelected: (pathname: string) => boolean;
  readonly href: string;
  readonly isGroup: false;
}

interface NavigationGroup {
  readonly label: string;
  readonly icon: React.ReactElement;
  readonly items: NavigationItem[];
  readonly isGroup: true;
}

type NavigationGroupOrItem = NavigationItem | NavigationGroup;

const navigationItems: readonly NavigationGroupOrItem[] = [
  {
    label: 'Dashboard',
    icon: <DashboardIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isDashboard(pathname),
    href: routes.DASHBOARD,
    isGroup: false,
  },
  {
    label: 'Crops',
    icon: <GrassIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isCrops(pathname),
    href: routes.CROPS,
    isGroup: false,
  },
  {
    label: 'Blends',
    icon: <AutoAwesomeIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isBlends(pathname),
    href: routes.BLENDS,
    isGroup: false,
  },
  {
    label: 'Recurring Orders',
    icon: <AutorenewRoundedIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isRecurringOrders(pathname),
    href: routes.RECURRING_ORDERS,
    isGroup: false,
  },
  {
    label: 'Orders',
    icon: <ShoppingCartIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isOrders(pathname),
    href: routes.ORDERS,
    isGroup: false,
  },
  {
    label: 'Recurring Tasks',
    icon: <ScheduleIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isRecurringTasks(pathname),
    href: routes.RECURRING_TASKS,
    isGroup: false,
  },
  {
    label: 'Tasks',
    icon: <TaskIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isTasks(pathname),
    href: routes.TASKS,
    isGroup: false,
  },
  {
    label: 'Recurring Plantings',
    icon: <CompostIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isRecurringPlantings(pathname),
    href: routes.RECURRING_PLANTINGS,
    isGroup: false,
  },
  {
    label: 'Plantings',
    icon: <ShelvesIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isPlantings(pathname),
    href: routes.PLANTINGS,
    isGroup: false,
  },
  {
    label: 'Harvests',
    icon: <AgricultureIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isHarvests(pathname),
    href: routes.HARVESTS,
    isGroup: false,
  },
  {
    label: 'Surpluses',
    icon: <WarehouseIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isSurplusesBuffers(pathname),
    href: routes.SURPLUSES_BUFFERS,
    isGroup: false,
  },
  {
    label: 'Calendar',
    icon: <CalendarMonthIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isCalendar(pathname),
    href: routes.CALENDAR,
    isGroup: false,
  },
  {
    label: 'Farm Report',
    icon: <AssessmentIcon fontSize="small" />,
    isSelected: (pathname: string) => routes.isFarmReport(pathname),
    href: routes.FARM_REPORT,
    isGroup: false,
  },
  {
    label: 'Other',
    icon: <MoreHorizIcon fontSize="small" />,
    isGroup: true,
    items: [
      {
        label: 'Blog',
        icon: <ArticleIcon fontSize="small" />,
        isSelected: (pathname: string) => routes.isBlog(pathname),
        href: routes.BLOG,
        isGroup: false,
      },
      {
        label: 'Tray Calculator',
        icon: <CalculateIcon fontSize="small" />,
        isSelected: (pathname: string) => routes.isTrayCalculator(pathname),
        href: routes.TRAY_CALCULATOR,
        isGroup: false,
      },
      {
        label: 'Price Calculator',
        icon: <PriceChangeIcon fontSize="small" />,
        isSelected: (pathname: string) => routes.isPriceCalculator(pathname),
        href: routes.PRICE_CALCULATOR,
        isGroup: false,
      },
      {
        label: 'Seed Density Calculator',
        icon: <MarginIcon fontSize="small" />,
        isSelected: (pathname: string) => routes.isSeedDensityCalculator(pathname),
        href: routes.SEED_DENSITY_CALCULATOR,
        isGroup: false,
      },
    ],
  },
];

const NavListItem = styled(ListItem)(() => ({
  display: 'block',
  padding: 0,
}));

const NavListButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isNested',
})<{ isNested?: boolean }>(({ theme, isNested }) => ({
  justifyContent: 'initial',
  ...(isNested && {
    paddingLeft: theme.spacing(4),
  }),
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
}));

const NavListIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isOpen',
})<{ isSelected?: boolean; isOpen: boolean }>(({ theme, isSelected, isOpen }) => ({
  minWidth: 0,
  marginRight: isOpen ? theme.spacing(3) : 0,
  transition: theme.transitions.create(['margin'], {
    duration: theme.transitions.duration.leavingScreen,
  }),
  justifyContent: 'center',
  color: isSelected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)',
}));

const NavListText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isOpen',
})<{ isSelected?: boolean; isOpen: boolean }>(({ theme, isSelected, isOpen }) => ({
  transition: theme.transitions.create(['opacity'], {
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '& .MuiListItemText-primary': {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  opacity: isOpen ? 1 : 0,
  color: isSelected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)',
}));

const NavigationItem = ({
  item,
  open,
  onClick,
}: { item: NavigationGroupOrItem; open: boolean; onClick: () => void }) => {
  const [expanded, setExpanded] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (item.isGroup) {
    // Don't show other group if not open
    if (!open) {
      return null;
    }
    return (
      <>
        <NavListItem>
          <NavListButton onClick={() => setExpanded(!expanded)}>
            <NavListIcon isSelected={false} isOpen={open}>
              {item.icon}
            </NavListIcon>
            <NavListText isSelected={false} isOpen={open} primary={item.label} />
            {open && <Chevron up={!expanded} />}
          </NavListButton>
        </NavListItem>
        <Collapse in={expanded && open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem) => (
              <NavListItem key={subItem.label}>
                <NavListButton
                  isNested
                  onClick={() => {
                    navigate(subItem.href);
                    onClick();
                  }}
                  selected={subItem.isSelected(location.pathname)}
                >
                  <NavListIcon isSelected={subItem.isSelected(location.pathname)} isOpen={open}>
                    {subItem.icon}
                  </NavListIcon>
                  <NavListText
                    isSelected={subItem.isSelected(location.pathname)}
                    isOpen={open}
                    primary={subItem.label}
                  />
                </NavListButton>
              </NavListItem>
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  const isSelected = item.isSelected(location.pathname);

  return (
    <NavListItem>
      <NavListButton
        onClick={() => {
          navigate(item.href);
          onClick();
        }}
        selected={isSelected}
      >
        <NavListIcon isSelected={isSelected} isOpen={open}>
          {item.icon}
        </NavListIcon>
        <NavListText isSelected={isSelected} isOpen={open} primary={item.label} />
      </NavListButton>
    </NavListItem>
  );
};

export const LoggedInLayout = () => {
  const { subscriptionStatus, freeTrialEndsAt } = useUserContext();
  const paymentStatus = getPaymentStatus({ subStatus: subscriptionStatus, freeTrialEndsAt });
  const showBanner = paymentStatus !== 'good';
  const [open, setOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const drawerContent = (
    <>
      <Toolbar />
      {/* Padding same as content padding below */}
      <List sx={{ paddingTop: { xs: 1, md: 2 }, paddingBottom: { xs: 1, md: 2 } }}>
        {navigationItems.map((item) => (
          <NavigationItem
            key={item.label}
            item={item}
            open={open}
            onClick={() => {
              // do nothing
            }}
          />
        ))}
      </List>
    </>
  );

  const mobileDrawerContent = (
    <>
      <Toolbar />
      <List sx={{ paddingTop: { xs: 1, sm: 2 }, paddingBottom: { xs: 1, sm: 2 } }}>
        {navigationItems.map((item) => (
          <NavigationItem key={item.label} item={item} open={mobileOpen} onClick={() => setMobileOpen(false)} />
        ))}
      </List>
    </>
  );

  return (
    <>
      <Helmet>
        {/* This should always be the same as the background-color of the AppBar above */}
        <meta name="theme-color" content="#001F3F" />
      </Helmet>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="fixed">
          <Toolbar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <IconButton
                color="inherit"
                size="large"
                aria-label="toggle drawer"
                onClick={() => {
                  setOpen(!open);
                }}
                edge="start"
                sx={{ mr: 2 }}
              >
                {open ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton
                color="inherit"
                size="large"
                aria-label="open drawer"
                onClick={() => setMobileOpen(!mobileOpen)}
                edge="start"
                sx={{ mr: 2 }}
              >
                {mobileOpen ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Microgreen Manager
            </Typography>
            <AccountOrLoginButton />
          </Toolbar>
        </AppBar>

        {/* This creates space for the AppBar */}
        <Toolbar />

        <Box sx={{ display: 'flex', flexGrow: 1, flexShrink: 1, flexBasis: '0%' }}>
          <Drawer variant="permanent" open={open} sx={{ display: { xs: 'none', md: 'block' } }}>
            {drawerContent}
          </Drawer>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sx={{
              display: { xs: 'block', md: 'none' },
            }}
          >
            {mobileDrawerContent}
          </Drawer>

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {showBanner && (
              <Box
                sx={{
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                  textAlign: 'center',
                  paddingTop: 1,
                  paddingBottom: 1,
                  paddingLeft: 2,
                  paddingRight: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'fontWeightBold', textWrap: 'balance' }}>
                  {paymentStatus === 'free_trial_ended'
                    ? 'Your free trial has ended. Please go to your account page to upgrade your account and continue using Microgreen Manager.'
                    : 'Your payment has failed. Please check your account page to update payment information.'}
                </Typography>
              </Box>
            )}
            <Box
              component="main"
              sx={{
                paddingTop: { xs: 1, md: 2 },
                paddingBottom: { xs: 1, md: 2 },
                paddingLeft: { xs: 1, sm: 2, lg: 3 },
                paddingRight: { xs: 1, sm: 2, lg: 3 },
                maxWidth: '1400px',
                width: '100%',
                margin: '0 auto',
              }}
            >
              <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                  <PageError error={utils.asErrorInstance(error)} retry={resetError} />
                )}
              >
                <Outlet />
              </Sentry.ErrorBoundary>
            </Box>
            <AppFooter />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoggedInLayout;
